import {
  createContext,
  useContext,
  useMemo,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import type {
  PackageImportSelection,
  PackageStageSelection,
} from '../../types/index';

type PackageCandidate = Record<string, any>;
type TagCandidate = Record<string, any>;

type PackagesWizardContextValue = {
  packageImportSelection: PackageImportSelection | null;
  packageCandidates: PackageCandidate[];
  stageSelection: PackageStageSelection | null;
  stageCandidates: TagCandidate[];
  resetPackageImportWizard: () => void;
  resetStageWizard: () => void;
  setPackageCandidates: (value: PackageCandidate[]) => void;
  setPackageImportSelection: (value: PackageImportSelection | null) => void;
  setStageCandidates: (value: TagCandidate[]) => void;
  setStageSelection: (value: PackageStageSelection | null) => void;
};

const PackagesWizardContext = createContext<PackagesWizardContextValue | null>(null);

export function PackagesWizardProvider({ children }: { children: ReactNode }) {
  const [packageImportSelection, setPackageImportSelection] = useState<PackageImportSelection | null>(null);
  const [packageCandidates, setPackageCandidates] = useState<PackageCandidate[]>([]);
  const [stageSelection, setStageSelection] = useState<PackageStageSelection | null>(null);
  const [stageCandidates, setStageCandidates] = useState<TagCandidate[]>([]);

  const value = useMemo(
    () => ({
      packageImportSelection,
      packageCandidates,
      stageSelection,
      stageCandidates,
      resetPackageImportWizard: () => {
        setPackageImportSelection(null);
        setPackageCandidates([]);
      },
      resetStageWizard: () => {
        setStageSelection(null);
        setStageCandidates([]);
      },
      setPackageCandidates,
      setPackageImportSelection,
      setStageCandidates,
      setStageSelection,
    }),
    [packageCandidates, packageImportSelection, stageCandidates, stageSelection]
  );

  return (
    <PackagesWizardContext.Provider value={value}>
      {children}
    </PackagesWizardContext.Provider>
  );
}

export function usePackagesWizard() {
  const context = useContext(PackagesWizardContext);
  if (!context) {
    throw new Error('usePackagesWizard must be used within PackagesWizardProvider');
  }
  return context;
}
